/**
 * ArticleHtml — an article body, as the public article page shows it.
 *
 * One place owns the typography so the admin editor's preview and the
 * public `/posts/<slug>` page cannot drift apart. There is no typography
 * plugin in this project, so the elements are addressed one by one.
 *
 * The HTML comes from the backend, which renders the owner's markdown
 * and sanitises it on every save (the editor's live preview escapes its
 * own output before adding marks). This component only styles it.
 */

const ARTICLE_HTML_CLASS = [
    // wrap-anywhere: a bare link whose text is its URL (the CTA autolink) breaks instead of panning the page sideways.
    'min-w-0 wrap-anywhere text-lg leading-relaxed text-gray-800',
    '[&_h1]:mt-10 [&_h1]:mb-3 [&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-900',
    '[&_h2]:mt-10 [&_h2]:mb-3 [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gray-900',
    '[&_h3]:mt-8 [&_h3]:mb-2 [&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-900',
    '[&_p]:mb-5',
    '[&_a]:text-primary-dark [&_a]:underline [&_a]:underline-offset-2',
    '[&_ul]:mb-5 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mb-5 [&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mb-1.5',
    '[&_blockquote]:my-6 [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600',
    '[&_img]:my-6 [&_img]:w-full [&_img]:rounded-xl',
    '[&_pre]:mb-5 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-gray-100 [&_pre]:p-4 [&_pre]:text-sm',
    '[&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:text-[0.9em]',
    '[&_hr]:my-8 [&_hr]:border-gray-200',
    '[&_strong]:font-semibold',
].join(' ');

export interface ArticleHtmlProps {
    /** Sanitised HTML — never raw visitor input. */
    html: string;
    className?: string;
    testId?: string;
}

export function ArticleHtml({ html, className = '', testId }: ArticleHtmlProps) {
    return (
        <div
            className={`${ARTICLE_HTML_CLASS} ${className}`}
            data-testid={testId}
            dangerouslySetInnerHTML={{ __html: html }}
        />
    );
}

export default ArticleHtml;
